import { useEffect, useState } from 'react';
import { ReviewInvitationsCandidateItem } from 'modules/sourcing-shared/shared-steps';
import { CampaignCreationSharedJobInformationFormData } from 'modules/campaign-creation-shared/forms';
import {
  SourcingSharedCustomizeMessageEmailFormData,
  SourcingSharedCustomizeMessageSmsFormData
} from 'modules/sourcing-shared/forms';
import { getSourcingSharedSubjectMessage } from 'modules/sourcing-shared/utils/getSourcingSharedSubjectMessage';
import { SourcingSharedCandidateBatch } from '../shared-stores/types';

type FilledSubjectItem = {
  invitationCandidateItemId: string;
  organizationName: string;
  customInvitationMessage: string;
  filledSubjectMessage: string;
};

type UseSourcingSharedFillSubjectMessageProps = {
  invitationCandidateItem: ReviewInvitationsCandidateItem;
  candidateBatch: SourcingSharedCandidateBatch;
  customizeMessageFormData:
    | SourcingSharedCustomizeMessageEmailFormData
    | SourcingSharedCustomizeMessageSmsFormData;
  jobInformationFormData: CampaignCreationSharedJobInformationFormData;
  jobPostingUrl: string | null;
  organizationName: string;
  onGenerateRelevance: (
    candidateId: string,
    candidateBatch: SourcingSharedCandidateBatch,
    customizeMessageFormData:
      | SourcingSharedCustomizeMessageEmailFormData
      | SourcingSharedCustomizeMessageSmsFormData
  ) => Promise<string>;
};

export const useSourcingSharedFillSubjectMessage = ({
  invitationCandidateItem,
  candidateBatch,
  customizeMessageFormData,
  jobInformationFormData,
  jobPostingUrl,
  organizationName,
  onGenerateRelevance
}: UseSourcingSharedFillSubjectMessageProps) => {
  const [filledSubjectItem, setFilledSubjectItem] =
    useState<FilledSubjectItem>();
  const [candidateRelevanceCache, setCandidateRelevanceCache] = useState<
    Record<string, string>
  >({});

  const getCandidateRelevance = async (): Promise<string> => {
    if (candidateRelevanceCache[invitationCandidateItem.id]) {
      return candidateRelevanceCache[invitationCandidateItem.id];
    }

    const canGenerateRelevance =
      customizeMessageFormData && jobInformationFormData;

    if (!canGenerateRelevance) {
      setCandidateRelevanceCache((prev) => ({
        ...prev,
        [invitationCandidateItem.id]: ''
      }));
      return '';
    }

    const candidateRelevance = await onGenerateRelevance(
      invitationCandidateItem.id,
      candidateBatch,
      customizeMessageFormData
    );
    setCandidateRelevanceCache((prev) => ({
      ...prev,
      [invitationCandidateItem.id]: candidateRelevance
    }));
    return candidateRelevance;
  };

  const { customInvitationMessage } = invitationCandidateItem;

  useEffect(() => {
    let isSuperseded = false;

    const fetchCandidateRelevance = async () => {
      // If it's already matching, skip
      if (
        invitationCandidateItem.id ===
          filledSubjectItem?.invitationCandidateItemId &&
        organizationName === filledSubjectItem?.organizationName &&
        customInvitationMessage === filledSubjectItem?.customInvitationMessage
      ) {
        return;
      }

      // If candidate has custom message use that
      if (customInvitationMessage) {
        setFilledSubjectItem({
          invitationCandidateItemId: invitationCandidateItem.id,
          organizationName,
          customInvitationMessage,
          filledSubjectMessage: customInvitationMessage
        });
        return;
      }

      const candidateRelevance = await getCandidateRelevance();

      if (isSuperseded) return;

      const filledSubjectMessage = getSourcingSharedSubjectMessage({
        customizeMessageFormData,
        candidate: invitationCandidateItem,
        jobInformationFormData,
        jobPostingUrl,
        candidateRelevance,
        organizationName
      });

      setFilledSubjectItem({
        invitationCandidateItemId: invitationCandidateItem.id,
        organizationName,
        customInvitationMessage,
        filledSubjectMessage
      });
    };

    fetchCandidateRelevance();

    return () => {
      isSuperseded = true;
    };
  }, [invitationCandidateItem.id, organizationName, customInvitationMessage]);

  const isSameIdentifier =
    invitationCandidateItem.id ===
      filledSubjectItem?.invitationCandidateItemId &&
    organizationName === filledSubjectItem?.organizationName &&
    customInvitationMessage === filledSubjectItem?.customInvitationMessage;

  return {
    filledSubjectMessage: isSameIdentifier
      ? filledSubjectItem?.filledSubjectMessage
      : ''
  };
};
